/**
 * Componente de imagen de especialidad con fallback
 */

import { useState } from "react";
import { Award } from "lucide-react";
import { getSpecialtyImageUrl } from "@/lib/utils";
import { cn } from "@/lib/utils";

interface SpecialtyImageProps {
  name: string;
  categorySlug?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const sizeClasses = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-16 h-16",
};

const iconSizeClasses = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-8 h-8",
};

export function SpecialtyImage({
  name,
  categorySlug,
  className,
  size = "md",
}: SpecialtyImageProps) {
  const [hasError, setHasError] = useState(false);
  const imageUrl = getSpecialtyImageUrl(name, categorySlug);

  // Obtener iniciales del nombre para el fallback
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  if (hasError) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex-shrink-0 font-semibold text-purple-600",
          sizeClasses[size],
          size === "sm" ? "text-xs" : size === "md" ? "text-sm" : "text-lg",
          className,
        )}
        title={name}
      >
        {initials || (
          <Award className={cn("text-purple-600", iconSizeClasses[size])} />
        )}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={name}
      title={name}
      className={cn(
        "object-contain rounded-lg flex-shrink-0 bg-gray-50",
        sizeClasses[size],
        className,
      )}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
}

export default SpecialtyImage;
